/** Core ShiftyPM platform modules on `companies/{id}.activeModules`. */
export const ACTIVE_MODULE_KEYS = [
  'ticketing',
  'agileKanban',
  'uptimeMonitoring',
  'portfolio',
  'vault',
  'codeReview',
  'chatWidget',
  'aiGameStudio',
  'aiStudio',
  'reports',
  'files',
  'crm',
];

/** @type {Record<string, boolean>} */
export const DEFAULT_ACTIVE_MODULES = {
  ticketing: true,
  agileKanban: false,
  uptimeMonitoring: false,
  portfolio: false,
  vault: false,
  codeReview: true,
  chatWidget: false,
  aiGameStudio: false,
  aiStudio: true,
  reports: true,
  files: true,
  crm: true,
};

export const ACTIVE_MODULE_LABELS = {
  ticketing: 'Ticketing',
  agileKanban: 'Agile & Kanban',
  uptimeMonitoring: 'Uptime Monitoring',
  portfolio: 'Product Portfolio',
  vault: 'Vault',
  codeReview: 'AI code review',
  chatWidget: 'Chat Support Widget',
  aiGameStudio: 'AI Game Studio',
  aiStudio: 'AI Studio',
  reports: 'Reports',
  files: 'Files',
  crm: 'CRM / Marketing',
};

export const ACTIVE_MODULE_DESCRIPTIONS = {
  ticketing: 'Support tickets and customer issue tracking',
  agileKanban: 'Sprint planning and kanban boards',
  uptimeMonitoring: 'Site and app liveness monitoring sweeps',
  portfolio: 'Product portfolio, infra costs, and AI analysis',
  vault: 'Secure credentials and secrets storage',
  codeReview: 'Review AI-proposed code patches before GitHub deployment',
  chatWidget: 'Embeddable public chat support widget for customer sites',
  aiGameStudio: 'AI-assisted game asset generation and Phaser project scaffolding',
  aiStudio: 'AI Workforce roster, agent prompts, and studio tools',
  reports: 'Enterprise reports hub, including Friction Insights review',
  files: 'Company file browser and storage nodes',
  crm: 'CRM campaigns, outreach, and marketing tools',
};

/**
 * Modules built and sold by ShiftyPM (not tenant-specific AI workers).
 * Same keys as ACTIVE_MODULE_KEYS — explicit alias for portfolio UI.
 */
export const BASE_PLATFORM_MODULE_KEYS = [...ACTIVE_MODULE_KEYS];

/**
 * Normalize `companies/{id}.customAiModules` (tenant-built AI modules).
 * @param {unknown} raw
 * @returns {Array<{
 *   id: string,
 *   name: string,
 *   description: string,
 *   status: string,
 *   agentId: string | null,
 *   agentName: string,
 * }>}
 */
export function normalizeCustomAiModules(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row, index) => {
      if (!row || typeof row !== 'object') return null;
      const name = typeof row.name === 'string' ? row.name.trim() : '';
      if (!name) return null;
      const id =
        typeof row.id === 'string' && row.id.trim()
          ? row.id.trim()
          : `custom-ai-${index}`;
      return {
        id,
        name,
        description:
          typeof row.description === 'string' ? row.description.trim() : '',
        status:
          typeof row.status === 'string' && row.status.trim()
            ? row.status.trim().toLowerCase()
            : 'active',
        agentId:
          typeof row.agentId === 'string' && row.agentId.trim()
            ? row.agentId.trim()
            : null,
        agentName:
          typeof row.agentName === 'string' ? row.agentName.trim() : '',
      };
    })
    .filter(Boolean);
}

/**
 * @param {Record<string, unknown> | null | undefined} raw
 * @returns {Record<string, boolean>}
 */
export function normalizeActiveModules(raw) {
  const out = { ...DEFAULT_ACTIVE_MODULES };
  if (!raw || typeof raw !== 'object') return out;
  for (const key of ACTIVE_MODULE_KEYS) {
    if (typeof raw[key] === 'boolean') out[key] = raw[key];
  }
  return out;
}

/** @param {{ activeModules?: Record<string, boolean>, enabledModules?: Record<string, boolean> } | null | undefined} workspace */
export function isActiveModuleEnabled(workspace, moduleKey) {
  if (!moduleKey || !workspace) return false;
  const am = workspace.activeModules || {};
  const em = workspace.enabledModules || {};
  switch (moduleKey) {
    case 'ticketing':
      return am.ticketing === true;
    case 'portfolio':
      return am.portfolio === true;
    case 'monitoring':
    case 'uptimeMonitoring':
      return am.monitoring === true || am.uptimeMonitoring === true;
    case 'agile':
    case 'agileKanban':
      return am.agile === true || am.agileKanban === true;
    case 'externalLinks':
      return am.externalLinks === true || em.externalLinks === true;
    case 'codeReview':
      return am.codeReview === true || am.codeReview === undefined;
    case 'chatWidget':
    case 'chatSupport':
      return am.chatWidget === true || am.chatSupport === true;
    case 'aiGameStudio':
      return am.aiGameStudio === true;
    case 'aiStudio':
      return typeof am.aiStudio === 'boolean' ? am.aiStudio : true;
    case 'reports':
      return typeof am.reports === 'boolean' ? am.reports : true;
    case 'files':
      return typeof am.files === 'boolean' ? am.files : true;
    case 'vault':
      return am.vault === true;
    case 'crm':
      return typeof am.crm === 'boolean' ? am.crm : true;
    case 'financeOps':
      return em.financeOps === true || am.financeOps === true;
    case 'aiInsights':
      return em.aiInsights === true || am.aiInsights === true;
    default:
      return Boolean(am[moduleKey]);
  }
}

/** Count of enabled hub modules (for admin tables). */
export function countActiveModules(activeModules) {
  const normalized = normalizeActiveModules(activeModules);
  return ACTIVE_MODULE_KEYS.filter((k) => normalized[k]).length;
}
